import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { InsertDriveFileOutlined, VisibilityOutlined } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : null;

/*
 * Compact summary of the data currently stored for a report:
 * which file, when, by whom, and a few counts. The rows themselves
 * are only rendered on demand, because reports can have thousands of rows.
 *
 * getSource(row) -> { fileName, uploadedAt } shows the latest uploaded file
 * with a View button that opens the stored rows that came from that file.
 */
const LatestUploadCard = ({
  title = "Currently Uploaded",
  fileName,
  uploadedAt,
  uploadedAtLabel = "Uploaded on",
  uploadedBy,
  loading,
  error,
  emptyMessage = "Nothing has been uploaded yet.",
  isEmpty,
  onRefresh,
  rows = [],
  columns = [],
  getSource,
}) => {
  // The rows shown in the dialog: { title, rows }, or null when it is closed.
  const [view, setView] = useState(null);

  // The latest uploaded file and its stored rows, or null.
  const latestFile = useMemo(() => {
    if (!getSource) {
      return null;
    }

    const fileMap = new Map();

    rows.forEach((row) => {
      const source = getSource(row) || {};
      // Same file name uploaded in the same minute is one upload (BOM lines are saved milliseconds apart).
      const minute = source.uploadedAt
        ? Math.floor(new Date(source.uploadedAt).getTime() / 60000)
        : "";
      const key = `${source.fileName || ""}|${minute}`;

      if (!fileMap.has(key)) {
        fileMap.set(key, {
          key,
          fileName: source.fileName || "File name not recorded",
          uploadedAt: source.uploadedAt,
          rows: [],
        });
      }

      const file = fileMap.get(key);

      if (
        source.uploadedAt &&
        (!file.uploadedAt || new Date(source.uploadedAt) > new Date(file.uploadedAt))
      ) {
        file.uploadedAt = source.uploadedAt;
      }

      file.rows.push(row);
    });

    return (
      Array.from(fileMap.values()).sort(
        (a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0),
      )[0] || null
    );
  }, [rows, getSource]);

  return (
    <Paper
      elevation={0}
      sx={{ border: "1px solid #e3e7ef", borderRadius: 2, p: 2.5 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <InsertDriveFileOutlined sx={{ color: "text.secondary" }} />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
            {/* The latest file below already shows its name and upload time. */}
            {!loading && !error && !isEmpty && !latestFile && (
              <Typography variant="body2" color="text.secondary">
                {[
                  fileName,
                  uploadedAt &&
                    `${uploadedAtLabel} ${formatDateTime(uploadedAt)}`,
                  uploadedBy && `by ${uploadedBy}`,
                ]
                  .filter(Boolean)
                  .join(" · ") || "Details of the stored data"}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : loading ? null : isEmpty ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {emptyMessage}
        </Typography>
      ) : null}

      {!loading && !error && !isEmpty && latestFile && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 1.5,
            py: 1,
            border: "1px solid #eef1f6",
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Latest file
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, wordBreak: "break-all" }}
            >
              {latestFile.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[
                latestFile.uploadedAt &&
                  `Uploaded on ${formatDateTime(latestFile.uploadedAt)}`,
                `${latestFile.rows.length.toLocaleString("en-IN")} row(s)`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>

          <Button
            size="small"
            startIcon={<VisibilityOutlined />}
            onClick={() =>
              setView({ title: latestFile.fileName, rows: latestFile.rows })
            }
          >
            View
          </Button>
        </Box>
      )}

      <Dialog
        open={Boolean(view)}
        onClose={() => setView(null)}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>{view?.title || fileName || title}</DialogTitle>
        <DialogContent>
          {view && (
            <DataGrid
              rows={view.rows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f7f9fc",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": { fontSize: "0.82rem" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LatestUploadCard;
